import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../../components/feature/PageLayout';
import PageHero from '../../../components/feature/PageHero';
import { submitLocalForm } from '../../../lib/localForms';

interface Kid {
  id: number;
  date_of_birth: string;
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
}

export default function MembershipFormPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [hasSpouse, setHasSpouse] = useState(false);
  const [kids, setKids] = useState<Kid[]>([]);

  const addKid = () => {
    setKids(prev => [
      ...prev,
      { id: Date.now(), date_of_birth: '', email: '', phone: '', first_name: '', last_name: '' }
    ]);
  };

  const removeKid = (id: number) => {
    setKids(prev => prev.filter(k => k.id !== id));
  };

  const updateKid = (id: number, field: keyof Omit<Kid, 'id'>, value: string) => {
    setKids(prev => prev.map(k => k.id === id ? { ...k, [field]: value } : k));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const urlEncodedData = new URLSearchParams();
    formData.forEach((value, key) => {
      urlEncodedData.append(key, value.toString());
    });

    kids.forEach((kid, index) => {
      urlEncodedData.append(`kid_${index + 1}_first_name`, kid.first_name);
      urlEncodedData.append(`kid_${index + 1}_last_name`, kid.last_name);
      urlEncodedData.append(`kid_${index + 1}_date_of_birth`, kid.date_of_birth);
      urlEncodedData.append(`kid_${index + 1}_email`, kid.email);
      urlEncodedData.append(`kid_${index + 1}_phone`, kid.phone);
    });

    try {
      const response = await submitLocalForm('Membership Form', urlEncodedData);

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setHasSpouse(false);
        setKids([]);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageLayout>
      <PageHero
        title="Membership Registration"
        subtitle="Register your family in the church database for feast cards, visitations and communications"
        backgroundImage="/assets/readdy/membership-hero-001-584ec4e5e018.jpg"
      />

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Header note */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-10 flex items-start gap-4">
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
            <i className="ri-information-line text-amber-600 text-2xl"></i>
          </div>
          <div>
            <p className="font-semibold text-gray-900 mb-1">Why register?</p>
            <p className="text-sm text-gray-600">
              Registering your family allows the church to send you feast day cards, coordinate pastoral visitations, and keep our community database up to date. All information is kept strictly confidential.
            </p>
          </div>
        </div>

        <form
          id="membership-form"
          data-local-form
          onSubmit={handleSubmit}
          className="space-y-10"
        >
          {/* ── Member Information ── */}
          <div className="bg-white rounded-2xl border border-stone-200 p-8">
            <h3 className="text-xl font-serif font-bold text-[#3d1a1e] mb-6 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-gold-100 text-gold-700 rounded-lg">
                <i className="ri-user-line text-lg"></i>
              </div>
              Member Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name <span className="text-red-500">*</span></label>
                <input type="text" name="first_name" required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="Enter your first name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Family Name <span className="text-red-500">*</span></label>
                <input type="text" name="family_name" required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="Enter your family name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth <span className="text-red-500">*</span></label>
                <input type="date" name="date_of_birth" required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address <span className="text-red-500">*</span></label>
                <input type="email" name="email" required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="your@email.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number <span className="text-red-500">*</span></label>
                <input type="tel" name="phone" required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="(514) 000-0000" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Home Address <span className="text-red-500">*</span></label>
                <input type="text" name="address" required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="Street address, city, province, postal code" />
              </div>
            </div>
          </div>

          {/* ── Spouse Section ── */}
          <div className="bg-white rounded-2xl border border-stone-200 p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-serif font-bold text-[#3d1a1e] flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-gold-100 text-gold-700 rounded-lg">
                  <i className="ri-heart-2-line text-lg"></i>
                </div>
                Spouse Information
              </h3>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <div
                  onClick={() => setHasSpouse(prev => !prev)}
                  className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${hasSpouse ? 'bg-burgundy-700' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${hasSpouse ? 'translate-x-7' : 'translate-x-1'}`}></div>
                </div>
                <span className="text-sm text-gray-600">{hasSpouse ? 'Added' : 'Add spouse'}</span>
              </label>
            </div>

            {!hasSpouse && (
              <p className="text-sm text-gray-500 italic">Toggle the switch above to add your spouse&apos;s information.</p>
            )}

            {hasSpouse && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Spouse First Name <span className="text-red-500">*</span></label>
                  <input type="text" name="spouse_first_name" required={hasSpouse} className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="Spouse&apos;s first name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Spouse Family Name <span className="text-red-500">*</span></label>
                  <input type="text" name="spouse_family_name" required={hasSpouse} className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="Spouse&apos;s family name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Spouse Date of Birth</label>
                  <input type="date" name="spouse_date_of_birth" className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Spouse Email Address</label>
                  <input type="email" name="spouse_email" className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="spouse@email.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Spouse Phone Number</label>
                  <input type="tel" name="spouse_phone" className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="(514) 000-0000" />
                </div>
              </div>
            )}
          </div>

          {/* ── Children Section ── */}
          <div className="bg-white rounded-2xl border border-stone-200 p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-serif font-bold text-[#3d1a1e] flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-gold-100 text-gold-700 rounded-lg">
                  <i className="ri-group-line text-lg"></i>
                </div>
                Children
              </h3>
              <button
                type="button"
                onClick={addKid}
                className="inline-flex items-center gap-2 px-4 py-2 bg-burgundy-700 text-white text-sm font-medium rounded-lg hover:bg-burgundy-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                Add Child
              </button>
            </div>

            {kids.length === 0 && (
              <p className="text-sm text-gray-500 italic">No children added yet. Click &quot;Add Child&quot; to add your children&apos;s information.</p>
            )}

            <div className="space-y-6">
              {kids.map((kid, index) => (
                <div key={kid.id} className="bg-stone-50 rounded-xl p-6 border border-stone-200 relative">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-semibold text-gray-700">Child {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeKid(kid.id)}
                      className="w-7 h-7 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line text-lg"></i>
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                      <input type="text" value={kid.first_name} onChange={e => updateKid(kid.id, 'first_name', e.target.value)} className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="First name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                      <input type="text" value={kid.last_name} onChange={e => updateKid(kid.id, 'last_name', e.target.value)} className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="Last name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                      <input type="date" value={kid.date_of_birth} onChange={e => updateKid(kid.id, 'date_of_birth', e.target.value)} className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                      <input type="email" value={kid.email} onChange={e => updateKid(kid.id, 'email', e.target.value)} className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="child@email.com" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                      <input type="tel" value={kid.phone} onChange={e => updateKid(kid.id, 'phone', e.target.value)} className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" placeholder="(514) 000-0000" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Status Messages ── */}
          {submitStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-green-600 text-xl flex-shrink-0 mt-0.5"></i>
              <div>
                <p className="text-green-800 font-semibold">Registration submitted successfully!</p>
                <p className="text-green-700 text-sm mt-1">Thank you for registering. The church office will follow up with you shortly.</p>
              </div>
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
              <i className="ri-error-warning-fill text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
              <div>
                <p className="text-red-800 font-semibold">Submission failed</p>
                <p className="text-red-700 text-sm mt-1">Please try again. If the issue persists, contact the church office directly.</p>
              </div>
            </div>
          )}

          {/* ── Actions ── */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/resources')}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 text-sm font-medium text-white bg-burgundy-700 rounded-xl hover:bg-burgundy-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Submitting...
                </span>
              ) : 'Submit Registration'}
            </button>
          </div>
        </form>
      </div>
    </PageLayout>
  );
}
